import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { communityService } from '../api/services';
import { useAuth } from '../context/AuthContext';
import useApi from '../hooks/useApi';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  Send, Video, Users, Plus, ArrowLeft, Loader2,
  Crown, Search, X, AlertCircle, Phone, MoreVertical,
  LogOut, UserPlus, Image, Paperclip, FileText,
  MessageCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';

// ── Message Bubble ────────────────────────────────────────────
const MessageBubble = ({ msg, isOwn }) => {
  const isSystem = msg.type === 'SYSTEM';
  const time     = new Date(msg.createdAt).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' });

  if (isSystem) return (
    <div className="flex justify-center my-2">
      <span className="text-[10px] text-forest-400 bg-cream-100 px-3 py-1 rounded-full">{msg.content}</span>
    </div>
  );

  return (
    <div className={`flex items-end gap-2 ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
      {!isOwn && (
        <div className="w-7 h-7 rounded-full bg-forest-600 flex items-center justify-center text-cream-50 text-[10px] font-bold flex-shrink-0 overflow-hidden mb-1">
          {msg.sender?.avatar
            ? <img src={msg.sender.avatar} alt="" className="w-full h-full object-cover" />
            : `${msg.sender?.firstName?.[0] ?? '?'}`}
        </div>
      )}
      <div className={`max-w-[70%] space-y-0.5`}>
        {!isOwn && (
          <p className="text-[10px] text-forest-400 ml-1">
            {msg.sender?.firstName} {msg.sender?.lastName}
            {msg.sender?.role === 'EXPERT' && <span className="text-amber-500 ml-1">· Expert</span>}
          </p>
        )}
        <div className={`rounded-2xl text-sm leading-relaxed overflow-hidden ${
          isOwn
            ? 'bg-forest-800 text-cream-50 rounded-br-sm'
            : 'bg-white border border-cream-200 text-forest-900 rounded-bl-sm'
        }`}>
          {msg.type === 'IMAGE' && msg.fileUrl ? (
            <a href={msg.fileUrl} target="_blank" rel="noopener noreferrer">
              <img src={msg.fileUrl} alt={msg.content}
                className="max-w-[200px] max-h-[200px] object-cover rounded-2xl cursor-pointer hover:opacity-90 transition-opacity" />
            </a>
          ) : msg.type === 'FILE' && msg.fileUrl ? (
            <a href={msg.fileUrl} target="_blank" rel="noopener noreferrer"
              className={`flex items-center gap-2.5 px-4 py-3 hover:opacity-80 transition-opacity ${
                isOwn ? 'text-cream-50' : 'text-forest-900'
              }`}>
              <FileText size={18} className="flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs font-medium truncate max-w-[160px]">{msg.content}</p>
                <p className={`text-[10px] ${isOwn ? 'text-forest-300' : 'text-forest-400'}`}>Click to download</p>
              </div>
            </a>
          ) : (
            <div className="px-4 py-2.5">{msg.content}</div>
          )}
        </div>
        <p className={`text-[9px] text-forest-300 ${isOwn ? 'text-right mr-1' : 'ml-1'}`}>{time}</p>
      </div>
    </div>
  );
};

// ── Invite Members Panel ──────────────────────────────────────
const InvitePanel = ({ communityId, onClose, onInvited }) => {
  const [search,   setSearch]   = useState('');
  const [users,    setUsers]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [selected, setSelected] = useState([]);
  const [inviting, setInviting] = useState(false);

  // Fetch users — called on mount and on search change
  const fetchUsers = async (q = '') => {
    setLoading(true);
    try {
      const res = await communityService.searchUsers(communityId, q);
      const d   = res.data?.data ?? res.data ?? {};
      const list = Array.isArray(d.users) ? d.users
        : Array.isArray(d) ? d : [];
      setUsers(list);
    } catch (err) {
      setUsers([]);
    } finally { setLoading(false); }
  };

  // Load all users on mount
  useEffect(() => { fetchUsers(''); }, [communityId]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => fetchUsers(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const toggle = (user) => {
    setSelected(s => s.find(u => u.id === user.id)
      ? s.filter(u => u.id !== user.id)
      : [...s, user]
    );
  };

  const handleInvite = async () => {
    if (!selected.length) return;
    setInviting(true);
    try {
      await communityService.invite(communityId, selected.map(u => u.id));
      toast.success(`${selected.length} member(s) added!`);
      onInvited();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not add members');
    } finally { setInviting(false); }
  };

  return (
    <>
      <div className="fixed inset-0 bg-forest-900/40 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-sm bg-cream-50 z-50 flex flex-col shadow-2xl"
        style={{ animation: 'slideInRight .2s ease' }}>
        <style>{`@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

        <div className="flex items-center justify-between px-5 py-4 bg-white border-b border-cream-200">
          <h3 className="font-serif font-semibold text-forest-900">Add Members</h3>
          <button onClick={onClose} className="text-forest-400 hover:text-forest-700"><X size={16} /></button>
        </div>

        <div className="px-4 py-3 bg-white border-b border-cream-100">
            <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-forest-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, email or business..."
              autoFocus
              className="w-full pl-8 pr-3 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 bg-white"
            />
            {search && (
              <button onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-forest-300 hover:text-forest-600">
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        {/* Selected chips */}
        {selected.length > 0 && (
          <div className="px-4 py-2 bg-white border-b border-cream-100 flex flex-wrap gap-1.5">
            {selected.map(u => (
              <span key={u.id} className="flex items-center gap-1 text-[10px] bg-forest-100 text-forest-700 px-2 py-1 rounded-full">
                {u.firstName} {u.lastName}
                <button onClick={() => toggle(u)} className="text-forest-400 hover:text-rust-500"><X size={9} /></button>
              </span>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {loading && <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-forest-400" /></div>}
          {!loading && users.map(u => {
            const isSelected = !!selected.find(s => s.id === u.id);
            const sub = u.role === 'EXPERT'
              ? (() => { try { const s = u.expertProfile?.specializations; return Array.isArray(s) ? s[0] : JSON.parse(s||'[]')[0]; } catch { return ''; } })()
              : u.clientProfile?.businessName;
            return (
              <div key={u.id}
                onClick={() => toggle(u)}
                className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                  isSelected ? 'bg-forest-50 border-2 border-forest-300' : 'bg-white border border-cream-200 hover:border-forest-200'
                }`}>
                <div className="w-9 h-9 rounded-xl bg-forest-700 flex items-center justify-center text-cream-50 font-serif font-bold text-sm overflow-hidden flex-shrink-0">
                  {u.avatar ? <img src={u.avatar} alt="" className="w-full h-full object-cover" /> : u.firstName[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-forest-900">{u.firstName} {u.lastName}</p>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                      u.role === 'EXPERT' ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'
                    }`}>{u.role === 'EXPERT' ? 'Expert' : 'Client'}</span>
                    {sub && <span className="text-[10px] text-forest-400 truncate">{sub}</span>}
                  </div>
                </div>
                {isSelected && <div className="w-5 h-5 bg-forest-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-[10px]">✓</span>
                </div>}
              </div>
            );
          })}
          {!loading && users.length === 0 && (
            <p className="text-center text-sm text-forest-400 py-8">
              {search ? `No results for "${search}"` : 'No users available to add'}
            </p>
          )}
        </div>

        <div className="px-4 py-4 bg-white border-t border-cream-100">
          <button onClick={handleInvite} disabled={!selected.length || inviting}
            className="btn-primary w-full justify-center flex items-center gap-2 disabled:opacity-50">
            {inviting ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
            Add {selected.length > 0 ? `${selected.length} Member${selected.length > 1 ? 's' : ''}` : 'Members'}
          </button>
        </div>
      </div>
    </>
  );
};

// ── Main Page ─────────────────────────────────────────────────
const CommunityDetailPage = () => {
  const { id }       = useParams();
  const { user }     = useAuth();
  const navigate     = useNavigate();

  const [messages,   setMessages]   = useState([]);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore,      setHasMore]      = useState(false);
  const [input,      setInput]      = useState('');
  const [sending,    setSending]    = useState(false);
  const [uploading,  setUploading]  = useState(false);
  const fileInputRef = useRef(null);
  const [showInvite, setShowInvite] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [refetch,    setRefetch]    = useState(0);
  const [startingCall, setStartingCall] = useState(false);

  const bottomRef          = useRef(null);
  const scrollContainerRef = useRef(null);
  const loadedPageRef      = useRef(1);   // most recent page loaded going backward
  const isNearBottomRef    = useRef(true);
  const initialLoadRef     = useRef(true);

  const { data, loading, error } = useApi(
    () => communityService.getById(id), [refetch]
  );
  const community = data?.data?.community ?? data?.community ?? data?.data ?? null;

  // Load message history — most recent page
  useEffect(() => {
    if (!id) return;
    loadedPageRef.current = 1;
    initialLoadRef.current = true;
    communityService.getMessages(id, { page: 1, limit: 50 })
      .then(res => {
        const body  = res.data?.data ?? res.data;
        const msgs  = body?.messages ?? [];
        const total = body?.total ?? msgs.length;
        setMessages(msgs);
        setHasMore(msgs.length < total);
      })
      .catch(() => {});
  }, [id, refetch]);

  // Load the next page further back in history, preserving scroll position
  const handleLoadOlder = async () => {
    if (loadingOlder || !hasMore) return;
    setLoadingOlder(true);
    const container = scrollContainerRef.current;
    const prevScrollHeight = container?.scrollHeight ?? 0;
    try {
      const nextPage = loadedPageRef.current + 1;
      const res   = await communityService.getMessages(id, { page: nextPage, limit: 50 });
      const body  = res.data?.data ?? res.data;
      const older = body?.messages ?? [];
      const total = body?.total ?? 0;

      if (older.length) {
        loadedPageRef.current = nextPage;
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          return [...older.filter(m => !existingIds.has(m.id)), ...prev];
        });
        setHasMore(nextPage * 50 < total);

        // Keep the view anchored on the same messages instead of
        // jumping — restore scroll position after the older batch
        // is prepended above what was already visible.
        requestAnimationFrame(() => {
          if (container) {
            container.scrollTop += container.scrollHeight - prevScrollHeight;
          }
        });
      } else {
        setHasMore(false);
      }
    } catch {
      // silent — user can just try scrolling up again
    } finally {
      setLoadingOlder(false);
    }
  };

  // Trigger "load older" when scrolled near the top; track whether
  // the user is near the bottom so polling doesn't yank them down
  // while they're reading back through history.
  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    if (el.scrollTop < 80) handleLoadOlder();
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
  };

  // Poll for new messages every 3 seconds (socket.io-client not installed).
  // Merges in only genuinely new messages — never replaces the array —
  // so history loaded via "load older" above is never discarded.
  const latestMsgIdRef = useRef(null);
  useEffect(() => {
    if (!id) return;
    const poll = async () => {
      try {
        const res  = await communityService.getMessages(id, { page: 1, limit: 50 });
        const body = res.data?.data ?? res.data;
        const msgs = body?.messages ?? [];
        if (!msgs.length) return;

        const latest = msgs[msgs.length - 1];
        if (latestMsgIdRef.current === latest.id) return;
        latestMsgIdRef.current = latest.id;

        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const newOnes = msgs.filter(m => !existingIds.has(m.id));
          if (!newOnes.length) return prev;
          return [...prev, ...newOnes];
        });
      } catch {}
    };
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [id]);

  // Auto-scroll to bottom — only on first load, when the user sent
  // the newest message themselves, or when they were already near
  // the bottom (so it doesn't yank someone reading older history).
  useEffect(() => {
    if (!messages.length) return;
    const last = messages[messages.length - 1];
    const lastIsOwn = last.senderId === user?.id || last.sender?.id === user?.id;
    if (initialLoadRef.current || lastIsOwn || isNearBottomRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: initialLoadRef.current ? 'auto' : 'smooth' });
    }
    initialLoadRef.current = false;
  }, [messages, user?.id]);

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!input.trim() || sending) return;
    const content = input.trim();
    setInput('');

    // Optimistic update
    const optimistic = {
      id:        `temp-${Date.now()}`,
      content,
      type:      'TEXT',
      senderId:  user?.id,
      createdAt: new Date().toISOString(),
      sender:    { id: user?.id, firstName: user?.firstName, lastName: user?.lastName, avatar: user?.avatar, role: user?.role },
    };
    setMessages(prev => [...prev, optimistic]);

    // Send via REST
    setSending(true);
    try {
      const res = await communityService.sendMessage(id, { content });
      const saved = res.data?.data?.message ?? res.data?.message;
      // Replace optimistic message with real one
      if (saved) {
        setMessages(prev => prev.map(m => m.id === optimistic.id ? saved : m));
      }
    } catch (err) {
      toast.error('Could not send message');
      setMessages(prev => prev.filter(m => m.id !== optimistic.id));
    } finally { setSending(false); }
  };

  const uploadingRef = useRef(false);
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || uploadingRef.current) return;

    // Clear input immediately to prevent double-fire
    const fileToUpload = file;
    e.target.value = '';

    // Max 50MB
    if (fileToUpload.size > 50 * 1024 * 1024) { toast.error('File too large — max 50MB'); return; }

    uploadingRef.current = true;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', fileToUpload);
      await communityService.uploadFile(id, fd);
      // Don't add optimistically — let the 3s poll pick it up to avoid duplicates
      toast.success('File sent!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not upload file');
    } finally {
      uploadingRef.current = false;
      setUploading(false);
    }
  };

  const handleStartCall = async () => {
    setStartingCall(true);
    try {
      navigate(`/communities/${id}/call`);
    } finally { setStartingCall(false); }
  };

  const handleLeave = async () => {
    if (!window.confirm('Leave this community?')) return;
    try {
      await communityService.leave(id);
      toast.success('Left community');
      navigate('/communities');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not leave');
    }
  };

  const isAdmin = community?.createdBy === user?.id;
  const members = community?.members ?? [];

  if (loading) return (
    <DashboardLayout title="Community">
      <div className="flex items-center justify-center py-20">
        <Loader2 size={28} className="animate-spin text-forest-600" />
      </div>
    </DashboardLayout>
  );

  if (error || !community) return (
    <DashboardLayout title="Community">
      <div className="text-center py-20">
        <AlertCircle size={36} className="mx-auto text-rust-400 mb-3" />
        <p className="font-serif text-xl text-forest-900">Community not found</p>
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout title={community.name}>
      <div className="max-w-3xl flex flex-col" style={{ height: 'calc(100vh - 140px)' }}>

        {/* Header */}
        <div className="flex items-center gap-3 pb-4 border-b border-cream-200 flex-shrink-0">
          <button onClick={() => navigate('/communities')}
            className="text-forest-400 hover:text-forest-700 transition-colors">
            <ArrowLeft size={18} />
          </button>

          <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 border border-cream-200">
            {community.imageUrl
              ? <img src={community.imageUrl} alt={community.name} className="w-full h-full object-cover" />
              : <div className="w-full h-full bg-forest-700 flex items-center justify-center">
                  <span className="font-serif font-bold text-cream-50">{community.name[0]}</span>
                </div>
            }
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-semibold text-forest-900 text-sm truncate">{community.name}</p>
            <button onClick={() => setShowMembers(!showMembers)}
              className="text-xs text-forest-400 hover:text-forest-700 flex items-center gap-1 transition-colors">
              <Users size={10} /> {members.length} member{members.length !== 1 ? 's' : ''}
            </button>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={handleStartCall} disabled={startingCall}
              className="flex items-center gap-1.5 text-xs font-medium bg-forest-800 text-cream-50 px-3 py-2 rounded-full hover:bg-forest-700 transition-colors disabled:opacity-60">
              <Video size={13} /> {startingCall ? 'Starting...' : 'Video Call'}
            </button>
            <button onClick={() => setShowInvite(true)}
              className="p-2 text-forest-400 hover:text-forest-700 border border-cream-200 rounded-full hover:bg-cream-50 transition-colors">
              <UserPlus size={15} />
            </button>
            <button onClick={handleLeave}
              className="p-2 text-forest-400 hover:text-rust-500 border border-cream-200 rounded-full hover:bg-cream-50 transition-colors"
              title="Leave community">
              <LogOut size={15} />
            </button>
          </div>
        </div>

        {/* Members bar (collapsible) */}
        {showMembers && (
          <div className="py-3 border-b border-cream-100 flex items-center gap-2 overflow-x-auto flex-shrink-0">
            {members.map(m => (
              <div key={m.id} className="flex flex-col items-center gap-1 flex-shrink-0">
                <div className="relative">
                  <div className="w-9 h-9 rounded-full overflow-hidden border-2 border-cream-200">
                    {m.user?.avatar
                      ? <img src={m.user.avatar} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full bg-forest-600 flex items-center justify-center text-cream-50 text-xs font-bold">
                          {m.user?.firstName?.[0]}
                        </div>
                    }
                  </div>
                  {m.role === 'ADMIN' && (
                    <Crown size={10} className="absolute -top-1 -right-1 text-amber-500" />
                  )}
                </div>
                <span className="text-[9px] text-forest-400 truncate max-w-[48px]">{m.user?.firstName}</span>
              </div>
            ))}
          </div>
        )}

        {/* Messages area */}
        <div ref={scrollContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto py-4 space-y-3 px-1">
          {loadingOlder && (
            <div className="flex justify-center py-2">
              <Loader2 size={14} className="animate-spin text-forest-400" />
            </div>
          )}
          {!loadingOlder && hasMore && (
            <div className="flex justify-center py-2">
              <button onClick={handleLoadOlder} className="text-xs text-forest-500 underline hover:text-forest-700">
                Load older messages
              </button>
            </div>
          )}
          {messages.length === 0 && (
            <div className="text-center py-16 text-forest-400">
              <MessageCircle size={32} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">No messages yet — say hello! 👋</p>
            </div>
          )}
          {messages.map(msg => (
            <MessageBubble
              key={msg.id}
              msg={msg}
              isOwn={msg.senderId === user?.id || msg.sender?.id === user?.id}
            />
          ))}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="pt-3 border-t border-cream-200 flex-shrink-0">
          <form onSubmit={handleSend} className="flex items-center gap-2">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.mp4,.mp3"
              onChange={handleFileUpload}
            />
            {/* Paperclip button */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              title="Attach file"
              className="w-11 h-11 border border-cream-300 hover:bg-cream-100 rounded-2xl flex items-center justify-center text-forest-500 transition-colors disabled:opacity-40 flex-shrink-0">
              {uploading
                ? <Loader2 size={15} className="animate-spin text-forest-600" />
                : <Paperclip size={15} />}
            </button>
            {/* Text input */}
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Type a message or attach a file..."
              className="flex-1 px-4 py-3 rounded-2xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white"
            />
            {/* Send button */}
            <button type="submit" disabled={!input.trim() || sending}
              className="w-11 h-11 bg-forest-800 hover:bg-forest-700 rounded-2xl flex items-center justify-center text-cream-50 transition-colors disabled:opacity-40 flex-shrink-0">
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            </button>
          </form>
        </div>
      </div>

      {showInvite && (
        <InvitePanel
          communityId={id}
          onClose={() => setShowInvite(false)}
          onInvited={() => setRefetch(r => r + 1)}
        />
      )}
    </DashboardLayout>
  );
};

export default CommunityDetailPage;